<template>
  <div v-if="currentLesson">
    <PrevNextLesson
      :hideNextButton="hideNextButton"
      :nextButtonDisabled="isNextLessonButtonDisabled"
      :nextButtonLabel="getNextArrowLabel"
      :hasAccess="canViewProductContent"
      :lesson="currentLesson"
      :lessons="productLessons"
      :product="currentProduct"
      :hidePrevButton="hidePrevButton"
      @prev="previousLesson"
      @next="nextLesson"
      @backToCourse="goBackToProductLessons"
    />
    <div
      v-if="
        typeOfLesson(currentLesson) === lessonType.TEXT ||
        typeOfLesson(currentLesson) === lessonType.QUIZ
      "
      class="container playlist__media-details d-flex"
    >
      <div class="left-column">
        <StudioNonMediaDetailsSidebar
          :sourceTitle="$t('PRODUCTS.LABELS.IN_THIS_LESSON')"
          :playlist="currentLesson"
          :media="currentMedia"
          class="d-lg-none mt-3 right-column"
        ></StudioNonMediaDetailsSidebar>
        <button
          v-if="!currentLesson.locked"
          class="btn d-sm-block d-md-none d-lg-none w-100 bg-brand-color c-white mt-3 mb-5"
          type="button"
          name="button"
          :disabled="isButtonDisabled"
          @click="completeAndContinue"
        >
          <font-awesome-icon class="mr-2" :icon="['fas', 'check']" />
          {{ $t('STUDIO.BUTTON.COMPLETE_AND_CONTINUE') }}
        </button>
        <div class="d-flex justify-content-between mb-4">
          <h1 class="title">{{ currentLesson.lesson_name }}</h1>
          <div
            v-if="isButtonDisabled"
            v-tooltip="{
              content: getLessonToolTip,
              delay: 100,
              placement: 'top',
              container: 'body',
            }"
          >
            <button
              v-if="!currentLesson.locked"
              class="btn d-none d-sm-none d-md-block d-lg-block bg-brand-color c-white ml-2"
              type="button"
              name="button"
              :disabled="isButtonDisabled"
              @click="completeAndContinue"
            >
              <font-awesome-icon class="mr-2" :icon="['fas', 'check']" />
              {{ $t('STUDIO.BUTTON.COMPLETE_AND_CONTINUE') }}
            </button>
          </div>
          <div v-else>
            <button
              v-if="!currentLesson.locked"
              class="btn d-none d-sm-none d-md-block d-lg-block bg-brand-color c-white ml-2"
              type="button"
              name="button"
              :disabled="isButtonDisabled"
              @click="completeAndContinue"
            >
              <font-awesome-icon class="mr-2" :icon="['fas', 'check']" />
              {{ $t('STUDIO.BUTTON.COMPLETE_AND_CONTINUE') }}
            </button>
          </div>
        </div>
        <div>
          <HMCustomContent
            class="mt-4"
            :html="currentLesson.lesson_description"
            :json="currentLesson.lesson_description_new"
          />
          <HMCustomContent
            class="mt-4"
            v-if="
              canViewProductContent &&
              !currentLesson.locked &&
              currentLesson.lesson_text
            "
            :json="currentLesson.lesson_text"
          />
          <div v-if="currentLesson.locked" class="mt-4">
            Content will become available once the lesson is unlocked
          </div>
          <div
            v-if="!currentLesson.locked && currentLesson.is_quiz_lesson"
            class="mt-4"
          >
            <StudioLessonQuizCard
              class="my-4"
              :status="currentLesson.quiz_status"
              @startQuiz="showQuizInModal"
            />
          </div>
        </div>
      </div>
      <StudioNonMediaDetailsSidebar
        :sourceTitle="$t('PRODUCTS.LABELS.IN_THIS_LESSON')"
        :playlist="currentLesson"
        :media="currentMedia"
        class="d-none d-lg-block right-column"
      ></StudioNonMediaDetailsSidebar>
    </div>
    <div
      v-if="typeOfLesson(currentLesson) === lessonType.MEDIA"
      class="img-container"
    >
      <div class="d-flex flex-wrap container">
        <StudioPlaylistMediaDetails
          :sourceTitle="$t('PRODUCTS.LABELS.IN_THIS_LESSON')"
          :source="getSource.LESSON"
          :media="currentMedia"
          :playlist="currentLesson"
          :isCompleteButtonDisabled="
            isMediaButtonDisabled || isNextLessonButtonDisabled
          "
          :activeMediaIndex="getActiveMediaIndex()"
          :lessonToolTip="getLessonToolTip()"
          class="playlist__media-details"
          @onPlaylistMediaRouteChanged="loadData"
          @completeAndContinue="completeAndContinue"
          @completeLesson="completeLesson"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import LegalWaiverModal from '@/web/components/shop/LegalWaiverModal';
import PrevNextLesson from '@/web/components/products/PrevNextLesson';
import RefreshData from '@/web/mixins/refreshData';
import { LESSON_START_TYPE_AFTER_PREVIOUS_LESSON } from '@/core/utils/products';
import { formatDate } from '@/web/utils/format';
import HMDialog from '@/web/components/common/HMDialog';
import { errorToMessage } from '@/core/utils/errors';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import StudioLessonQuizCard from '@/web/components/products/StudioLessonQuizCard';
import QuizModal from '@/web/components/quizzes/QuizModal';
import { getLessonType } from '@/core/utils/products';
import LessonType from '@/core/typings/enums/LessonType';
import StudioPlaylistMediaDetails from '@/web/components/products/StudioPlaylistMediaDetails';
import RightBlockSourceType from '@/core/typings/enums/RightBlockSourceType';
import StudioNonMediaDetailsSidebar from '@/web/components/products/StudioNonMediaDetailsSidebar';

export default Vue.extend({
  name: 'StudioLessonDetail',
  mixins: [RefreshData],
  components: {
    PrevNextLesson,
    StudioLessonQuizCard,
    StudioPlaylistMediaDetails,
    StudioNonMediaDetailsSidebar,
  },
  data() {
    return {
      lessonCompleted: false,
      isMediaButtonDisabled: false,
      isNonMediabuttonDisabled: false,
    };
  },
  computed: {
    ...mapGetters('product', ['productLessons']),
    ...mapGetters({
      canViewProductContent: 'product/canViewProductContent',
      currentProduct: 'product/product',
    }),
    currentLesson() {
      if (!this.productLessons) {
        return;
      }
      return this.productLessons.find(
        lesson => lesson.id == this.$route.params.lesson_id
      );
    },
    currentLessonIndex() {
      if (!this.productLessons) {
        return;
      }
      return this.productLessons.findIndex(
        lesson => lesson.id == this.$route.params.lesson_id
      );
    },
    currentMedia() {
      if (!this.currentLesson || !this.currentLesson.content) {
        return;
      }
      return this.currentLesson.content.find(
        media => media.id == this.$route.params.media_id
      );
    },
    nextLessonData() {
      return this.productLessons[this.currentLessonIndex + 1];
    },
    isNextLessonLocked() {
      return this.nextLessonData?.locked;
    },
    isNextLessonUnlockable() {
      if (
        this.currentLesson.is_quiz_lesson &&
        this.currentLesson.pass_quiz_to_progress &&
        this.currentLesson.quiz_status !== 'passed'
      ) {
        return false;
      }
      return (
        this.nextLessonData?.lesson_start_type ===
        LESSON_START_TYPE_AFTER_PREVIOUS_LESSON
      );
    },
    isNextLessonButtonDisabled() {
      return this.isNextLessonLocked && !this.isNextLessonUnlockable;
    },
    isNextLessonEmpty() {
      return !this.nextLessonData?.content?.length;
    },
    hideNextButton() {
      return this.currentLessonIndex === this.productLessons.length - 1;
    },
    hidePrevButton() {
      return this.currentLessonIndex == 0;
    },
    getNextButtonLabel() {
      if (!this.nextLessonData) return '';
      if (!this.isNextLessonLocked) return this.$t('STUDIO.LABELS.NEXT_LESSON');
      if (this.isNextLessonUnlockable)
        return this.$t('STUDIO.LABELS.NEXT_LESSON');
      return this.$t('STUDIO.LABELS.NEXT_LESSON_WILL_BE_AVAILABLE_AFTER_DATE', {
        date: this.formatDate(this.nextLessonData.lesson_available_after_date),
      });
    },
    getNextArrowLabel() {
      if (!this.nextLessonData) return '';
      if (!this.isNextLessonLocked) return this.$t('STUDIO.LABELS.NEXT_LESSON');
      if (this.isNextLessonUnlockable)
        return this.$t('STUDIO.LABELS.NEXT_LESSON');
      return this.$t('STUDIO.LABELS.AVAILABLE_AFTER_DATE', {
        date: this.formatDate(this.nextLessonData.lesson_available_after_date),
      });
    },
    studioURL() {
      return getStudioURL(this.$route);
    },
    lessonType() {
      return LessonType;
    },
    getSource() {
      return RightBlockSourceType;
    },
    isButtonDisabled() {
      return (
        (this.currentLesson.pass_quiz_to_progress &&
          this.currentLesson.quiz_status !== 'passed') ||
        this.isNextLessonButtonDisabled
      );
    },
  },
  methods: {
    typeOfLesson(currentLesson) {
      return getLessonType(currentLesson);
    },
    formatDate(date) {
      return formatDate(date, 'date-word', '');
    },
    async goToNextLesson() {
      const nextLesson = this.productLessons[this.currentLessonIndex + 1];
      if (!nextLesson) {
        this.goBackToProductLessons();
        return;
      }

      this.redirectToLesson(nextLesson);
    },
    async nextLesson() {
      if (this.isNextLessonLocked && !this.isNextLessonUnlockable) {
        // TODO this should not really happen, or there is an error in navigation
        alert('Next lesson is still locked');
        return;
      }
      if (this.isNextLessonLocked && this.isNextLessonUnlockable) {
        const title = this.$t('GENERAL.TITLE.CONFIRM');
        const subtitle = this.$t('STUDIO.TITLE.MARK_LESSON_AS_COMPLETE');
        const text = this.$t(
          'STUDIO.TEXT.MARK_LESSON_AS_COMPLETE_AND_CONTINUE_TO_NEXT_LESSON'
        );
        const discardButton = this.$t('GENERAL.BUTTON.GO_BACK');
        const confirmButton = this.$t('GENERAL.BUTTON.CONTINUE');
        const closable = false;

        const discard = () => {};
        const confirm = async () => {
          try {
            await this.$store.dispatch('product/checkLessonAsCompleted', {
              studioURL: this.studioURL,
              productId: this.currentLesson.product,
              lessonId: this.currentLesson.id,
            });
            await this.goToNextLesson();
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Marking lesson complete failed',
              text: errorToMessage(error?.response, `Request failed: ${error}`),
              duration: -1, // stay ON
            });
          }
        };

        this.$modal.show(HMDialog, {
          title,
          subtitle,
          text,
          discardButton,
          confirmButton,
          discard,
          confirm,
          closable,
        });
      } else if (!this.isNextLessonLocked) {
        this.goToNextLesson();
      }
    },
    async previousLesson() {
      const previousLesson = this.productLessons[this.currentLessonIndex - 1];
      if (!previousLesson) {
        this.goBackToProductLessons();
        return;
      }

      this.redirectToLesson(previousLesson);
    },
    goBackToProductLessons() {
      this.$router.push({
        name: 'studio-product-lessons',
        params: { product_id: this.$route.params.product_id },
      });
    },
    async loadData() {
      const waiver = await this.$store.dispatch('studio/getWaiverToBeSigned', {
        studioURL: this.studioURL,
      });
      if (waiver.id) {
        this.$modal.show(
          LegalWaiverModal,
          { showCloseButton: false, waiver },
          { clickToClose: false, classes: 'vm--inline-modal' }
        );
      }
      try {
        if (!this.currentLesson) {
          await this.$store.dispatch('product/getProductDetail', {
            studioURL: getStudioURL(this.$route),
            productId: this.$route.params.product_id,
          });
        }
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('STUDIO.NOTIFICATIONS.LOAD_LESSON_MEDIA_ERROR'),
          text: `Error: ${error}`,
          duration: -1, // stay ON
        });
      }
    },
    async showQuizInModal() {
      const studioURL = getStudioURL(this.$route);
      const quizId = this.currentLesson.quiz;
      try {
        const quiz = await this.$store.dispatch('studentSolvedQuiz/getQuiz', {
          studioURL,
          quizId,
        });
        this.$modal.show(
          QuizModal,
          {
            studioURL,
            quizId,
            quiz,
          },
          { classes: ['vm--inline-modal'], clickToClose: false },
          {
            finish: this.updateLessonQuizStatus,
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: `Failed to load quiz ${quizId}`,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    updateLessonQuizStatus(solvedQuiz) {
      this.$store.commit('product/UPDATE_LESSON_QUIZ_STATUS', {
        lesson_id: this.currentLesson.id,
        status: solvedQuiz.status,
      });
    },
    completeLesson() {
      this.$store.dispatch('product/checkLessonAsCompleted', {
        studioURL: this.studioURL,
        productId: this.currentLesson.product,
        lessonId: this.currentLesson.id,
      });
    },
    redirectToLesson(lesson, index = 0) {
      const lessonType = getLessonType(lesson);
      if (lessonType === LessonType.TEXT || lessonType === LessonType.QUIZ) {
        this.$router.replace({
          name: 'studio-lesson-non-media',
          params: {
            product_id: this.$route.params.product_id,
            lesson_id: lesson?.id,
          },
        });
      } else if (lessonType === LessonType.MEDIA) {
        this.$router.push({
          name: 'studio-lesson-media',
          params: {
            product_id: this.$route.params.product_id,
            lesson_id: lesson?.id,
            media_id: lesson?.content[index]?.id,
          },
        });
      }
    },
    getActiveMediaIndex() {
      let index = null;
      this.currentLesson.content.forEach((media, mediaIndex) => {
        if (this.$route.params.media_id == media.id) {
          index = mediaIndex;
        }
      });
      return index;
    },
    async completeAndContinue() {
      const lessonType = getLessonType(this.currentLesson);
      if (
        this.isNextLessonLocked &&
        !this.isNextLessonUnlockable &&
        lessonType === LessonType.MEDIA
      ) {
        await this.goThroughMedia('error');
      }
      if (this.isNextLessonLocked && this.isNextLessonUnlockable) {
        if (lessonType === LessonType.MEDIA) {
          await this.goThroughMedia(true);
        } else {
          await this.completeLesson();
          await this.goToNextLesson();
        }
      } else if (!this.isNextLessonLocked) {
        // It works not so way as nextLesson does
        if (lessonType === LessonType.MEDIA) {
          await this.goThroughMedia(true);
        } else {
          await this.completeLesson();
          await this.goToNextLesson();
        }
      }
    },
    async goThroughMedia(canComplete) {
      const activeMediaIndex = this.getActiveMediaIndex();
      if (
        Number.isInteger(activeMediaIndex) &&
        activeMediaIndex + 1 == this.currentLesson?.content.length
      ) {
        // if it's the last elem go to the next lesson -> go to the next lesson
        if (canComplete === true) {
          await this.completeLesson();
          await this.goToNextLesson();
        } else {
          await this.goToNextLesson();
        }
      } else {
        // Else -> go thgrough media list
        if (canComplete === 'error') {
          this.isMediaButtonDisabled = true;
        }
        this.redirectToLesson(this.currentLesson, activeMediaIndex + 1);
      }
    },
    getLessonToolTip() {
      const lessonType = getLessonType(this.currentLesson);
      if (this.isButtonDisabled && lessonType === LessonType.QUIZ) {
        return this.$t(
          'STUDIO.NOTIFICATIONS.PASS_THE_QUIZ_TO_COMPLETE_THIS_LESSON'
        );
      } else {
        return this.getNextButtonLabel;
      }
    },
  },
  watch: {
    currentLesson: {
      handler(lesson) {
        this.lessonCompleted = lesson?.completed;
      },
      immediate: true,
    },
  },
  mounted() {
    this.loadData();
  },
  beforeDestroy() {
    this.$modal.hideAll();
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.img-container {
  @include media-breakpoint-down(sm) {
    margin-left: -1rem;
    margin-right: -1rem;
  }
}

.playlist__media-details {
  flex-wrap: wrap;
  flex-direction: row;
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-top: 3em;
  margin-bottom: 3em;

  @include media-breakpoint-down(md) {
    flex-direction: column;
    margin-top: 0;
  }
}

.container {
  &__panel {
    @include media-breakpoint-down(md) {
      margin-left: 0;
      margin-right: 0;
      width: 100%;
    }
  }

  &__media {
    flex-direction: column !important;
    cursor: pointer;
    border-radius: $border-radius;
    &:hover,
    &.active {
      background-color: var(
        --brand-color-with-opacity-03,
        rgba($light-bubblegum, 0.3)
      );
    }
  }

  &__title {
    border: 1px solid $light-gray;
    border-radius: 8px 8px 0 0;

    &--small-text {
      font-size: 12px;
      color: $dark-gray;
    }
  }

  &__content {
    min-height: 80px;
    max-height: 350px;
    overflow: auto;
    border: 1px solid $light-gray;
    border-top: 1px solid transparent;
    border-radius: 0 0 8px 8px;

    &--h-260 {
      height: 260px;
    }
  }
}

.left-column {
  width: 65%;
  @include media-breakpoint-down(md) {
    width: 100%;
  }
}

.right-column {
  width: 32%;
  @include media-breakpoint-down(md) {
    width: 100%;
  }
}

.title {
  flex: 1;
}
</style>
